import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Button,
  Stack,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppLoading from "verticales/AppLoading.tsx";
import fetchData from "verticales/FetchData";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
const context = "ec2-get-agente";

const hoursOptions = Array.from({ length: 24 }, (_, i) => i);

const ViewNewEdit = (props) => {
  const original = props.row?.original || {};
  const [blocking, setBlocking] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Campos principales
  const [cliente, setCliente] = useState(original.cliente || "");
  const [account, setAccount] = useState(original.account || "");
  const [estado, setEstado] = useState(original.estado || "enable");

  // Campos JSONB de horarios
  const [horasCalientes, setHorasCalientes] = useState(original.horas_calientes || []);
  const [horasFrias, setHorasFrias] = useState(original.horas_frias || []);
  const [horasApagadoForzoso, setHorasApagadoForzoso] = useState(
    original.horas_apagado_forzoso || []
  );
  const [horaApagadoFinal, setHoraApagadoFinal] = useState(original.hora_apagado_final || 22);

  // Instancias
  const [instancesArray, setInstancesArray] = useState(
    original.instances_array || [{ principal: true, instance_id: "" }]
  );

  // ⚙️ Función para agregar una instancia nueva
  const handleAddInstance = () => {
    setInstancesArray([...instancesArray, { principal: false, instance_id: "" }]);
  };

  // ⚙️ Eliminar instancia
  const handleRemoveInstance = (index) => {
    const updated = [...instancesArray];
    updated.splice(index, 1);
    setInstancesArray(updated);
  };

  // ⚙️ Guardar registro
  const handleAccept = async () => {
    if (!cliente || !account) {
      setErrorMessage("El cliente y la cuenta son requeridos");
      return;
    }
    setBlocking(true);
    const params = {
      action: original.id ? "UPDATE" : "INSERT",
      object: {
        id: original.id || -1,
        cliente,
        account,
        estado,
        horas_calientes: horasCalientes,
        horas_frias: horasFrias,
        horas_apagado_forzoso: horasApagadoForzoso,
        hora_apagado_final: horaApagadoFinal,
        instances_array: instancesArray,
      },
    };
    try {
      const resp = await fetchData(url, context, params);
      console.log("Guardado:", resp);
      props.handleCloseModal(true);
    } catch (e) {
      console.error("Error al guardar:", e);
      setErrorMessage("Error al guardar los datos.");
    } finally {
      setBlocking(false);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}></Grid>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <MDTypography variant="h4" color="white">
                Configuración instancias
              </MDTypography>
              <IconButton
                aria-label="close"
                onClick={() => props.handleCloseModal(false)}
                sx={(theme) => ({ color: theme.palette.common.white })}
              >
                <CloseIcon />
              </IconButton>
            </MDBox>

            {blocking ? (
              <AppLoading titleCustom="Guardando..." />
            ) : (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {/* 🧾 Datos generales */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cliente/grupo"
                      value={cliente}
                      onChange={(e) => setCliente(e.target.value)}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cuenta"
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={["enable", "disabled"]}
                      value={estado}
                      onChange={(event, newValue) => setEstado(newValue)}
                      renderInput={(params) => <TextField {...params} label="Estado" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Hora Apagado Final"
                      type="number"
                      value={horaApagadoFinal}
                      onChange={(e) => setHoraApagadoFinal(Number(e.target.value))}
                      inputProps={{ min: 0, max: 23 }}
                      fullWidth
                    />
                  </Grid>

                  {/* 🔥 Horarios JSONB */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Horas Calientes"
                      value={JSON.stringify(horasCalientes)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          if (Array.isArray(parsed) && parsed.every((n) => typeof n === "number")) {
                            setHorasCalientes(parsed);
                            setErrorMessage("");
                          }
                        } catch {
                          setErrorMessage(
                            "Formato inválido. Debe ser un array de números, ej: [6,7,8]"
                          );
                        }
                      }}
                      placeholder="Ej: [6,7,8,9]"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Horas Frías"
                      value={JSON.stringify(horasFrias)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          if (Array.isArray(parsed) && parsed.every((n) => typeof n === "number")) {
                            setHorasFrias(parsed);
                            setErrorMessage("");
                          }
                        } catch {
                          setErrorMessage("Formato inválido. Debe ser un array de números");
                        }
                      }}
                      placeholder="Ej: [0,1,2]"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Apagado Forzoso"
                      value={JSON.stringify(horasApagadoForzoso)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          if (Array.isArray(parsed) && parsed.every((n) => typeof n === "number")) {
                            setHorasApagadoForzoso(parsed);
                            setErrorMessage("");
                          }
                        } catch {
                          setErrorMessage("Formato inválido. Debe ser un array de números");
                        }
                      }}
                      placeholder="Ej: [22,23]"
                      fullWidth
                    />
                  </Grid>

                  {/* 💻 Instancias */}
                  <Grid item xs={12}>
                    <Typography variant="h6" mt={2}>
                      Instancias
                    </Typography>
                  </Grid>
                  {instancesArray.map((inst, index) => (
                    <Grid container spacing={1} key={index} alignItems="center" sx={{ pl: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Instance ID"
                          value={inst.instance_id}
                          onChange={(e) => {
                            const newArray = [...instancesArray];
                            newArray[index].instance_id = e.target.value;
                            setInstancesArray(newArray);
                          }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={inst.principal}
                              onChange={(e) => {
                                const newArray = [...instancesArray];
                                newArray[index].principal = e.target.checked;
                                setInstancesArray(newArray);
                              }}
                            />
                          }
                          label="Principal"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <IconButton onClick={() => handleRemoveInstance(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Button onClick={handleAddInstance} color="primary">
                      + Agregar Instancia
                    </Button>
                  </Grid>

                  {/* ⚠️ Mensajes y acciones */}
                  {errorMessage && (
                    <Grid item xs={12}>
                      <Typography color="error">{errorMessage}</Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
                      <MDButton onClick={() => props.handleCloseModal(false)}>Cancelar</MDButton>
                      <MDButton color="info" onClick={handleAccept}>
                        Guardar
                      </MDButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Card>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};
ViewNewEdit.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }),
  handleCloseModal: PropTypes.func.isRequired,
};

export default ViewNewEdit;
